'use client';
import React from 'react';
import styled, { css } from '../references';
import { ScrollContainer } from './scrollContainer';
import ReactDOM from 'react-dom';
import { DROPDOWN_MIN_WIDTH, getViewportRect, resolveConstrainedFloaterLayout } from './constrainedFloaterUtils';

export type { AnchorRect, DropdownLayout } from './constrainedFloaterUtils';
export { computeDropdownLayout } from './constrainedFloaterUtils';

interface Props {
  allowOverflowParent?: boolean;
  /** When provided with allowOverflowParent, layout is computed from viewport/anchor (single source of truth) */
  anchorRect?: { left: number; right: number };
  anchorWidth?: number;
  children?: React.ReactNode;
  className?: string;
  flightMode?: boolean;
  forceMaxWidth?: boolean;
  minHeight?: number;
  maxWidth?: number;
  minWidth?: number | `100%`;
  parentRectClassName?: string;
  positionLeft?: number | null;
  positionRight?: number | null;
  positionTop?: number | null;
  zIndex?: number;
  floatingRootRef?: React.Ref<HTMLDivElement | null>;
}

const findParentWith = (constrainingDiv: HTMLElement, predicate: (el: HTMLElement) => boolean) => {
  let parentEl = constrainingDiv.parentElement;
  let parentRect = {
    height: 0,
    left: 0,
    top: 0,
    width: 0,
  };

  let scrollVisible = false;
  while (parentEl) {
    const clientHeight = parentEl?.clientHeight ?? 0;
    const scrollHeight = parentEl?.scrollHeight ?? 0;
    if (clientHeight < scrollHeight) {
      scrollVisible = true;
    }
    if (predicate(parentEl)) {
      // Bounding rect has numbers for width/height, whereas style can have 'auto'
      parentRect = parentEl.getBoundingClientRect();
      break;
    }
    parentEl = parentEl.parentElement;
  }

  return { parentEl, parentRect, scrollVisible };
};

export const ConstrainingDiv = (
  constrainingDiv: HTMLDivElement,
  maxWidth?: number,
  minWidth?: number | `100%`,
  minHeight?: number,
  parentRectClassName?: string,
  positionRight?: number | null,
  forceMaxWidth?: boolean,
  allowOverflowParent?: boolean,
  positionTop?: number | null,
) => {
  const refRect = constrainingDiv.getBoundingClientRect();

  const { parentEl, parentRect, scrollVisible } = findParentWith(constrainingDiv, parentEl => {
    const style = getComputedStyle(parentEl);
    return parentRectClassName ? parentEl.classList.contains(parentRectClassName) : style.overflow === 'hidden';
  });

  const rect = allowOverflowParent ? { ...parentRect, ...getViewportRect() } : parentRect;

  const remainingWidth =
    positionRight || positionRight === 0 ? refRect.right - rect.left : rect.left + rect.width - refRect.left;

  const remainingHeight =
    allowOverflowParent && positionTop != null
      ? Math.max(minHeight ?? 0, rect.height - (positionTop - rect.top))
      : Math.max(minHeight ?? 0, rect.height - (refRect.top - rect.top));
  const finalMaxWidth =
    positionRight || positionRight === 0
      ? remainingWidth
      : remainingWidth - (scrollVisible && !allowOverflowParent ? 12 : 0);

  return {
    bottom: parentRect.top + parentRect.height,
    left: parentRect.left,
    maxHeight: remainingHeight,
    maxWidth: forceMaxWidth
      ? (maxWidth ?? Infinity)
      : Math.min(maxWidth ?? Infinity, finalMaxWidth < 0 ? Infinity : finalMaxWidth),
    minWidth:
      minWidth === `100%`
        ? parentRect.width < DROPDOWN_MIN_WIDTH
          ? DROPDOWN_MIN_WIDTH
          : parentRect.width
        : Math.min(minWidth ?? Infinity, finalMaxWidth < 0 ? Infinity : finalMaxWidth),
    parent: parentEl,
    right: parentRect.left + parentRect.width,
    top: parentRect.top,
  };
};

/**
 * Creates a floating div whose size is restricted so that it will fit in a parent
 * with overflow: hidden ensuring that no part of its children will be hidden
 * due to extending outside of said parent.
 */
export const ConstrainedFloater: React.FC<Props> = props => {
  const { forceMaxWidth, maxWidth, minHeight, minWidth, positionLeft, positionRight } =
    resolveConstrainedFloaterLayout(props);

  const [constrainedStyle, setConstrainedStyle] = React.useState<{
    bottom?: number;
    left?: number;
    maxHeight?: number;
    minHeight?: number;
    maxWidth?: number;
    minWidth?: number;
    parent?: HTMLElement | null;
    right?: number;
    top?: number;
    viewPort?: {
      bottom: number;
      left: number;
      right: number;
      top: number;
    };
  }>({
    maxWidth: props.maxWidth,
  });

  // for resize and zoom of browser
  const [resizeTrigger, setResizeTrigger] = React.useState(0);
  const resizeListener = React.useCallback(() => {
    setResizeTrigger(resizeDependency => resizeDependency + 1);
  }, []);
  React.useEffect(() => {
    window.addEventListener('resize', resizeListener);
    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [resizeListener]);

  const onConstrainingDiv = React.useCallback(
    (constrainingDiv: HTMLDivElement | null) => {
      if (constrainingDiv) {
        const { parentRect } = findParentWith(constrainingDiv, parentEl => {
          const style = getComputedStyle(parentEl);
          return style.backdropFilter ? style.backdropFilter !== 'none' : false;
        });
        const containerDiv = ConstrainingDiv(
          constrainingDiv,
          maxWidth,
          minWidth,
          minHeight,
          props.parentRectClassName,
          positionRight,
          forceMaxWidth,
          props.allowOverflowParent,
          props.positionTop,
        );

        setConstrainedStyle({
          ...containerDiv,
          bottom: containerDiv.bottom,
          left: containerDiv.left,
          right: containerDiv.right,
          top: containerDiv.top,
          viewPort: {
            bottom: parentRect.top + parentRect.height,
            left: parentRect.left,
            right: parentRect.left + parentRect.width,
            top: parentRect.top,
          },
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      maxWidth,
      minWidth,
      minHeight,
      forceMaxWidth,
      props.allowOverflowParent,
      props.parentRectClassName,
      props.positionTop,
      positionRight,
      resizeTrigger,
    ],
  );

  const onFloaterRoot = React.useCallback(
    (el: HTMLDivElement | null) => {
      onConstrainingDiv(el);
      const r = props.floatingRootRef;
      if (!r) return;
      if (typeof r === 'function') {
        r(el);
      } else {
        (r as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }
    },
    [onConstrainingDiv, props.floatingRootRef],
  );

  const Content = (
    <ConstrainedFloaterStyle
      $left={
        positionLeft !== undefined
          ? positionLeft === null
            ? `${constrainedStyle.left ? constrainedStyle.left - (constrainedStyle.viewPort?.left ?? 0) : 0}px`
            : `${positionLeft ? positionLeft - (constrainedStyle.viewPort?.left ?? 0) : 0}px`
          : 'auto'
      }
      $maxHeight={constrainedStyle.maxHeight && `${constrainedStyle.maxHeight}px`}
      $maxWidth={constrainedStyle.maxWidth && `${constrainedStyle.maxWidth}px`}
      $minHeight={minHeight != null ? `${minHeight}px` : undefined}
      $minWidth={constrainedStyle.minWidth && `${constrainedStyle.minWidth}px`}
      $right={
        positionRight !== undefined
          ? positionRight === null
            ? `${constrainedStyle.right ? constrainedStyle.right - (constrainedStyle.viewPort?.right ?? 0) : 0}px`
            : `${positionRight ? positionRight - (constrainedStyle.viewPort?.right ?? 0) : 0}px`
          : 'auto'
      }
      $top={
        props.positionTop !== undefined
          ? props.positionTop === null
            ? `${constrainedStyle.top ? constrainedStyle.top - (constrainedStyle.viewPort?.top ?? 0) : 0}px`
            : `${props.positionTop ? props.positionTop - (constrainedStyle.viewPort?.top ?? 0) : 0}px`
          : 'auto'
      }
      $zIndex={props.zIndex}
      ref={onFloaterRoot}
    >
      <ScrollContainer className={props.className}>{props.children}</ScrollContainer>
    </ConstrainedFloaterStyle>
  );

  if (props.allowOverflowParent && typeof document !== 'undefined') {
    return ReactDOM.createPortal(Content, document.body);
  }
  if (props.parentRectClassName && constrainedStyle.parent) {
    return ReactDOM.createPortal(Content, constrainedStyle.parent);
  }
  return Content;
};

interface ConstrainedFloaterStyleProps {
  $left: React.CSSProperties['left'];
  $maxHeight: React.CSSProperties['maxHeight'];
  $maxWidth: React.CSSProperties['maxWidth'];
  $minHeight?: React.CSSProperties['minHeight'];
  $minWidth: React.CSSProperties['minWidth'];
  $right: React.CSSProperties['right'];
  $top: React.CSSProperties['top'];
  $zIndex?: number;
}

const ConstrainedFloaterStyle = styled.div.attrs<ConstrainedFloaterStyleProps>(props => ({
  $left: props.$left,
  $maxHeight: props.$maxHeight,
  $maxWidth: props.$maxWidth,
  $minHeight: props.$minHeight,
  $minWidth: props.$minWidth,
  $right: props.$right,
  $top: props.$top,
  $zIndex: props.$zIndex,
}))`
  display: flex;
  ${props =>
    props.$maxHeight &&
    css`
      max-height: ${props.$maxHeight};
    `};
  ${props =>
    props.$maxWidth &&
    css`
      max-width: ${props.$maxWidth};
    `};
  ${props =>
    props.$minWidth &&
    css`
      min-width: ${props.$minWidth};
    `};
  ${props =>
    props.$minHeight &&
    css`
      min-height: ${props.$minHeight};
    `};
  overflow: hidden;
  position: fixed;
  left: ${props => props.$left ?? 'auto'};
  right: ${props => props.$right ?? 'auto'};
  top: ${props => props.$top ?? '100%'};
  z-index: ${props => props.$zIndex ?? 100};
`;
